import React from 'react';
import {
  ArrowLeft,
  ArrowRight,
  Newspaper,
  Cpu,
  Boxes,
  Smartphone,
  Trophy,
  TrendingUp,
  Linkedin,
  Mail,
  Phone,
  Sparkles,
} from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BackToTop from '../components/BackToTop';

const HERO_IMAGE  = 'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/lt5gv2ku_PRESS%20HERO%20IMAGE.png';
const HRI_PHOTO   = 'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/urvdehjg_HRI%20HANDSHAKE.png';
const DELOITTE    = 'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/dh2xzivl_DELOITTE.png';
const OPEX_BADGE  = 'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/xeh79t7y_OPEX%20IMAGE%20VERSION%203.png';

const AWARD_OPEX  = 'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/a66cffus_USE%204.png';
const AWARD_IT    = 'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/m61gqvsf_USE%205.png';
const AWARD_DEL   = 'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/r6gphsak_USE%206.png';
const AWARD_ECOM  = 'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/vqv532a4_AWARD%204.jpg';

const FEATURED = {
  category: 'Technology Investment',
  icon: Cpu,
  date: 'Latest',
  title: 'eCOMM Invests In Next-Generation Payment Technology Infrastructure',
  excerpt: "eCOMM Merchant Solutions continues to invest in the modernisation of its payment technology platform to support faster, more reliable and scalable payment services. Current initiatives include enhancements to transaction processing infrastructure, API capabilities and digital payment technologies designed to improve merchant and customer payment experiences.",
  tag: 'Announcement',
};

const ARTICLES = [
  {
    category: 'Product Expansion',
    icon: Boxes,
    date: 'Latest',
    title: 'Expansion Of Integrated Hospitality Solutions',
    excerpt: "eCOMM continues to enhance its EPOS and EPOS Mini product range, helping merchants combine ordering, payment acceptance and transaction management within a single streamlined solution.",
    tag: 'Product',
  },
  {
    category: 'Terminal Rollout',
    icon: Smartphone,
    date: 'Latest',
    title: 'Terminal Portfolio Expansion — NexGO N96 Rollout',
    excerpt: "eCOMM has begun rolling out the latest NexGO N96 Android payment terminal as part of its continued investment in modern payment technologies. Larger touchscreen, improved performance and enhanced mobility features.",
    tag: 'Product',
  },
  {
    category: 'Mobile Payments',
    icon: Smartphone,
    date: 'Latest',
    title: 'Flexible Mobile Payments — NexGO N62 MPOS Solution',
    excerpt: "eCOMM continues to expand its mobile payment offering through the NexGO N62 MPOS terminal — a compact and portable solution designed for businesses that require flexible payment acceptance on the move.",
    tag: 'Product',
  },
  {
    category: 'Partnership',
    icon: TrendingUp,
    date: '',
    title: 'Horse Racing Ireland Partners With eCOMM Merchant Solutions',
    excerpt: "HRI partners with eCOMM as payment provider to its four racecourses — Fairyhouse, Leopardstown, Navan and Tipperary.",
    image: HRI_PHOTO,
    tag: 'News',
  },
  {
    category: 'Award',
    icon: Trophy,
    date: '',
    title: 'eCOMM Included In Deloitte Fast 50 — 3rd Year Running',
    excerpt: 'eCOMM Merchant Solutions has been included in the Deloitte Fast 50 list of the top 50 fastest-growing technology companies in Ireland for the third consecutive year.',
    image: DELOITTE,
    tag: 'Recognition',
  },
  {
    category: 'Award',
    icon: Trophy,
    date: '',
    title: 'Operational Excellence Award For Innovation 2021',
    excerpt: "eCOMM was awarded the Operational Excellence Award for Innovation in 2021, recognising operational excellence in the payments sector.",
    image: OPEX_BADGE,
    tag: 'Recognition',
  },
];

const AWARDS_STRIP = [
  { logo: AWARD_OPEX, name: 'Operational Excellence', year: '2021' },
  { logo: AWARD_IT,   name: 'Irish Times Innovation', year: '2020' },
  { logo: AWARD_DEL,  name: 'Deloitte Technology Fast 50', year: '2021' },
  { logo: AWARD_ECOM, name: 'Best Payments Startup', year: 'Winner' },
];

const Eyebrow = ({ children }) => (
  <p className="text-[11px] font-bold text-[#ef5b25] uppercase tracking-[0.22em] mb-3">{children}</p>
);

const SectionHeading = ({ eyebrow, title, light = false }) => (
  <div className="mb-8">
    <Eyebrow>{eyebrow}</Eyebrow>
    <h2 className={`text-3xl sm:text-4xl lg:text-[2.5rem] font-bold leading-tight tracking-tight ${light ? 'text-white' : 'text-[#0a1929]'}`}>{title}</h2>
    <div className="h-1 w-20 bg-[#ef5b25] rounded-full mt-4" />
  </div>
);

const ArticleCard = ({ article, i }) => {
  const Icon = article.icon;
  return (
    <article
      className="group flex flex-col h-full rounded-2xl bg-white border border-gray-100 shadow-[0_10px_30px_-8px_rgba(31,78,121,0.18)] hover:shadow-[0_18px_38px_-8px_rgba(31,78,121,0.28)] hover:-translate-y-1 hover:border-[#1F4E79]/25 transition-all duration-300 overflow-hidden"
      data-testid={`press-article-${i}`}
    >
      {article.image ? (
        <div className="relative h-44 sm:h-48 overflow-hidden bg-gradient-to-br from-[#1F4E79]/8 to-[#ef5b25]/8">
          <img src={article.image} alt={article.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
          <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/95 backdrop-blur-sm shadow text-[#1F4E79] text-[10px] font-bold uppercase tracking-[0.15em]">
            <Icon size={11} strokeWidth={2.5} />
            {article.category}
          </div>
        </div>
      ) : (
        <div className="relative h-44 sm:h-48 bg-gradient-to-br from-[#0a1929] via-[#0e2540] to-[#163a5f] flex items-center justify-center overflow-hidden">
          <div className="pointer-events-none absolute -top-12 -right-12 w-40 h-40 rounded-full bg-[#ef5b25]/25 blur-3xl" aria-hidden="true" />
          <Icon size={56} className="relative text-[#ffcba6]/85" strokeWidth={1.5} />
          <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/15 backdrop-blur-sm border border-white/20 text-[#ffcba6] text-[10px] font-bold uppercase tracking-[0.15em]">
            {article.category}
          </div>
        </div>
      )}
      <div className="flex-1 flex flex-col p-5 sm:p-6">
        <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.15em] mb-3">
          <span className="text-[#ef5b25]">{article.tag}</span>
          {article.date && (
            <>
              <span className="text-gray-300">·</span>
              <span className="text-gray-500">{article.date}</span>
            </>
          )}
        </div>
        <h3 className="font-bold text-[#0a1929] text-lg leading-snug mb-3 group-hover:text-[#1F4E79] transition-colors duration-200">{article.title}</h3>
        <p className="text-[14px] text-gray-600 leading-relaxed flex-1">{article.excerpt}</p>
      </div>
    </article>
  );
};

const AboutPressMedia = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fromTopNav = location.state?.from === 'top-nav';
  const FeaturedIcon = FEATURED.icon;

  return (
    <div className="min-h-screen flex flex-col bg-white" data-testid="about-press-media-page">
      <Header />
      <div className="h-20" />

      {/* ╔═════════════════════ HERO ═════════════════════╗ */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#f7f9fc] via-white to-[#eef3f8]" data-testid="press-hero">
        <div className="pointer-events-none absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#1F4E79]/8 blur-3xl" aria-hidden="true" />
        <div className="pointer-events-none absolute -bottom-40 -left-32 w-96 h-96 rounded-full bg-[#ef5b25]/8 blur-3xl" aria-hidden="true" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-20 sm:pt-16 sm:pb-24">
          <button
            onClick={() => (fromTopNav ? navigate('/') : navigate(-1))}
            className="flex items-center gap-2 text-gray-600 hover:text-[#1F4E79] font-medium mb-10 transition-colors duration-200"
            data-testid="press-back-button"
          >
            <ArrowLeft size={18} />
            {fromTopNav ? 'Back to Home' : 'Back'}
          </button>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-white/80 via-white/95 to-white/80 backdrop-blur-md border border-white/80 text-[#1F4E79] text-[11px] font-bold uppercase tracking-[0.22em] mb-6 shadow-[0_4px_20px_-2px_rgba(31,78,121,0.18)] ring-1 ring-inset ring-white/60">
                <Newspaper size={13} className="text-[#ef5b25]" strokeWidth={2} />
                Press & Media
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-[64px] font-bold text-[#0a1929] leading-[1.05] tracking-tight mb-6" data-testid="press-title">
                eCOMM Updates &amp;<br />
                <span className="text-[#1F4E79]">Media Resources.</span>
              </h1>
              <p className="text-lg sm:text-xl text-gray-600 leading-relaxed max-w-2xl mb-10 font-medium">
                Stay informed with the latest announcements, product developments and company news from eCOMM Merchant Solutions. Explore the stories, innovations and milestones shaping our journey as we continue to simplify payments and support businesses across multiple channels.
              </p>
              <div className="flex">
                <Link
                  to="/contact-sales"
                  onClick={() => window.__resetSourceScroll?.()}
                  className="group inline-flex items-center justify-center gap-2.5 px-7 py-3.5 bg-[#1F4E79] text-white font-bold rounded-lg hover:bg-[#163a5f] hover:gap-3.5 transition-all duration-200 shadow-md hover:shadow-lg"
                  data-testid="press-cta-enquiries"
                >
                  Media Enquiries</Link>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="relative max-w-md mx-auto lg:mx-0">
                <div className="absolute -inset-4 bg-gradient-to-br from-[#1F4E79]/15 to-[#ef5b25]/15 rounded-3xl blur-2xl pointer-events-none" aria-hidden="true" />
                <div className="relative rounded-2xl overflow-hidden border border-white/60 shadow-2xl">
                  <img src={HERO_IMAGE} alt="Modern glass corporate skyscraper" className="w-full h-auto object-cover aspect-square" />
                  <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-[#0a1929]/90 via-[#0a1929]/55 to-transparent p-5 pt-14">
                    <p className="text-[10px] font-bold text-[#ffcba6] uppercase tracking-[0.2em] mb-1">News &amp; Communications</p>
                    <p className="text-white font-bold text-xl leading-tight">A Growing Payments Story.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ╔═════════════════════ INTRO ═════════════════════╗ */}
      <section className="bg-white py-20 sm:py-24" data-testid="press-intro">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <SectionHeading eyebrow="News & Communications" title="The latest from eCOMM." />
          </div>
          <div className="lg:col-span-7 space-y-5 text-gray-700 leading-relaxed text-base sm:text-lg">
            <p>
              Stay up to date with the latest developments from eCOMM Merchant Solutions, including product launches, company announcements, technology initiatives and industry recognition.
            </p>
            <p>
              As a growing payments technology provider, we continue to support businesses through innovative payment solutions across online, mobile and in-person commerce channels.
            </p>
          </div>
        </div>
      </section>

      {/* ╔═════════════════════ FEATURED + GRID ═════════════════════╗ */}
      <section className="bg-[#f7f9fc] py-20 sm:py-24" data-testid="press-featured">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <Eyebrow>Latest News &amp; Announcements</Eyebrow>
              <h2 className="text-3xl sm:text-4xl lg:text-[2.25rem] font-bold leading-tight tracking-tight text-[#0a1929]">In The Spotlight.</h2>
              <div className="h-1 w-20 bg-[#ef5b25] rounded-full mt-3" />
            </div>
          </div>

          {/* Featured card — full width */}
          <article
            className="group grid grid-cols-1 lg:grid-cols-12 gap-0 rounded-2xl bg-white border border-gray-100 shadow-[0_18px_50px_-12px_rgba(31,78,121,0.25)] hover:shadow-[0_24px_60px_-12px_rgba(31,78,121,0.35)] overflow-hidden transition-all duration-300 mb-8"
            data-testid="press-featured-article"
          >
            <div className="lg:col-span-5 relative bg-gradient-to-br from-[#0a1929] via-[#0e2540] to-[#163a5f] min-h-[280px] lg:min-h-[360px] flex items-center justify-center overflow-hidden">
              <div className="pointer-events-none absolute -top-20 -right-20 w-72 h-72 rounded-full bg-[#ef5b25]/30 blur-3xl" aria-hidden="true" />
              <div className="pointer-events-none absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-[#1F4E79]/45 blur-3xl" aria-hidden="true" />
              <FeaturedIcon size={96} className="relative text-[#ffcba6]/90" strokeWidth={1.25} />
              <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/15 backdrop-blur-sm border border-white/20 text-[#ffcba6] text-[10px] font-bold uppercase tracking-[0.18em]">
                Featured Announcement
              </div>
            </div>
            <div className="lg:col-span-7 p-7 sm:p-10 flex flex-col justify-center">
              <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] mb-4">
                <span className="text-[#ef5b25]">{FEATURED.tag}</span>
                <span className="text-gray-300">·</span>
                <span className="text-gray-500">{FEATURED.category}</span>
                <span className="text-gray-300">·</span>
                <span className="text-gray-500">{FEATURED.date}</span>
              </div>
              <h3 className="font-bold text-[#0a1929] text-2xl sm:text-3xl leading-tight mb-4">{FEATURED.title}</h3>
              <p className="text-[15px] sm:text-base text-gray-600 leading-relaxed">{FEATURED.excerpt}</p>
              <p className="text-[14px] text-gray-500 leading-relaxed italic mt-4">
                These ongoing developments form part of eCOMM's long-term strategy to build flexible and future-ready payment solutions across online, mobile and in-store commerce channels.
              </p>
            </div>
          </article>

          {/* 5-article grid (3+2 split) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 items-start">
            {ARTICLES.map((a, i) => <ArticleCard key={i} article={a} i={i} />)}
          </div>
        </div>
      </section>

      {/* ╔═════════════════════ AWARDS & INDUSTRY RECOGNITION ═════════════════════╗ */}
      <section className="bg-white py-20 sm:py-24" data-testid="awards-recognition">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12 text-center mx-auto">
            <Eyebrow>Awards & Industry Recognition</Eyebrow>
            <h2 className="text-3xl sm:text-4xl lg:text-[2.5rem] font-bold leading-tight tracking-tight text-[#0a1929]">Recognised Across The Industry.</h2>
            <div className="h-1 w-20 bg-[#ef5b25] rounded-full mt-4 mx-auto mb-5" />
            <p className="text-lg text-gray-700 leading-relaxed">
              eCOMM has been recognised across the payments and technology sector for innovation, operational excellence and business growth.
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {AWARDS_STRIP.map((a, i) => (
              <div
                key={i}
                className="group rounded-xl bg-gradient-to-br from-[#f7f9fc] to-white border border-gray-100 p-6 flex flex-col items-center text-center hover:shadow-lg hover:border-[#ef5b25]/30 transition-all duration-200"
                data-testid={`award-strip-${i}`}
              >
                <div className="h-44 sm:h-48 w-full flex items-center justify-center mb-5 px-2">
                  <img src={a.logo} alt={a.name} className="max-h-[170px] max-w-full object-contain" />
                </div>
                <p className="font-bold text-[#0a1929] text-sm leading-tight mb-1">{a.name}</p>
                <p className="text-[11px] font-bold text-[#ef5b25] uppercase tracking-[0.15em]">{a.year}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ╔═════════════════════ MEDIA ENQUIRIES ═════════════════════╗ */}
      <section id="media-enquiries" className="bg-[#f7f9fc] py-20 sm:py-24" data-testid="media-enquiries">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <SectionHeading eyebrow="Media Enquiries" title="Get in touch with our team." />
            <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
              For press, partnership or media-related enquiries, please contact the eCOMM communications team.
            </p>
          </div>

          <div className="lg:col-span-7 rounded-2xl bg-white border border-gray-100 shadow-lg p-7 sm:p-10">
            <h3 className="text-lg font-bold text-[#0a1929] mb-6">Get in Touch</h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="flex items-start gap-3" data-testid="contact-phone">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-[#1F4E79]/10 flex items-center justify-center">
                  <Phone size={18} className="text-[#1F4E79]" strokeWidth={1.75} />
                </div>
                <div>
                  <p className="text-[11px] font-bold text-[#ef5b25] uppercase tracking-[0.15em] mb-1">Phone Support</p>
                  <p className="text-gray-800 text-sm font-medium">EU: <a href="tel:+353469011288" className="hover:text-[#1F4E79] transition-colors">+353 (0)46 901 1288</a></p>
                  <p className="text-gray-800 text-sm font-medium">UK: <a href="tel:+442071835253" className="hover:text-[#1F4E79] transition-colors">+44 (0)20 7183 5253</a></p>
                </div>
              </div>

              <div className="flex items-start gap-3" data-testid="contact-email">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-[#1F4E79]/10 flex items-center justify-center">
                  <Mail size={18} className="text-[#1F4E79]" strokeWidth={1.75} />
                </div>
                <div>
                  <p className="text-[11px] font-bold text-[#ef5b25] uppercase tracking-[0.15em] mb-1">Email</p>
                  <a
                    href="mailto:csr@ecomm365.com"
                    className="text-[#1F4E79] hover:text-[#163a5f] underline text-sm font-medium transition-colors duration-200"
                  >
                    csr@ecomm365.com
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ╔═════════════════════ STAY CONNECTED ═════════════════════╗ */}
      <section className="py-16 sm:py-20 bg-white" data-testid="stay-connected">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-2xl bg-gradient-to-br from-[#0a1929] via-[#0e2540] to-[#163a5f] p-8 sm:p-14 text-center shadow-xl relative overflow-hidden">
            <div className="pointer-events-none absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#ef5b25]/15 blur-3xl" aria-hidden="true" />
            <div className="pointer-events-none absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-[#1F4E79]/25 blur-3xl" aria-hidden="true" />
            <div className="relative">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur-sm border border-white/15 text-[#ffcba6] text-[11px] font-bold uppercase tracking-[0.22em] mb-5">
                <Sparkles size={12} />
                Stay Connected
              </div>
              <h3 className="text-3xl sm:text-4xl lg:text-[2.5rem] font-bold text-white leading-tight tracking-tight mb-4">
                Follow Our Journey.
              </h3>
              <p className="text-white/90 text-base sm:text-lg leading-relaxed max-w-2xl mx-auto mb-8">
                Follow the latest developments, product updates and company announcements from eCOMM Merchant Solutions.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
                <a
                  href="https://www.linkedin.com/company/ecomm-merchant-solutions"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center justify-center gap-2.5 px-7 py-3.5 bg-white text-[#0A66C2] font-bold rounded-lg hover:bg-white/90 hover:gap-3.5 transition-all duration-200 shadow-md hover:shadow-lg"
                  data-testid="stay-connected-linkedin"
                >
                  <Linkedin size={18} fill="currentColor" />
                  Follow on LinkedIn</a>
                <Link
                  to="/contact-sales"
                  onClick={() => window.__resetSourceScroll?.()}
                  className="inline-flex items-center justify-center gap-2 px-7 py-3.5 border-2 border-white/30 text-white font-bold rounded-lg hover:bg-white/10 hover:border-white/50 transition-all duration-200"
                  data-testid="stay-connected-contact"
                >
                  Contact Us
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <BackToTop />
    </div>
  );
};

export default AboutPressMedia;
